import { Typography } from "@/components/ui/typography"
import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { IconAlertTriangle } from "@tabler/icons-react"
import type { InquiryStatus } from "../../../api/supplierInquiriesApi"
import { regulationKeyLabels } from "../../../utils/regulationLabel"

// What happens next: reminders chase the rest, the user does once no automatic
// reminder will go out (handed back, or reminders off), or a reply stating it is being
// reviewed. Any other open status (a dead address) only names what is open.
function nextStep(
  status: InquiryStatus,
  autoRemindersEffective: boolean,
): TranslationKeys {
  if (status === "ureview") return "MC_INQ_OPEN_REGULATIONS_REVIEW"
  if (status === "escalated") return "MC_INQ_OPEN_REGULATIONS_MANUAL"
  if (status === "waiting" || status === "reminded")
    return autoRemindersEffective
      ? "MC_INQ_OPEN_REGULATIONS_WAITING"
      : "MC_INQ_OPEN_REGULATIONS_MANUAL"
  return "MC_INQ_OPEN_REGULATIONS"
}

/** Says which regulations a partial answer left open (TUR-1732) and what to do: the
 *  inquiry went back to the supplier instead of closing. Renders nothing when nothing
 *  is open (`openRegulations` is null or empty). */
export function InquiryOpenRegulationsNote({
  openRegulations,
  status,
  autoRemindersEffective,
}: {
  openRegulations?: readonly string[] | null
  status: InquiryStatus
  autoRemindersEffective: boolean
}) {
  const { translate } = useTranslation()
  if (!openRegulations?.length) return null

  const list = regulationKeyLabels(openRegulations, translate)
  return (
    <div role="status" className="flex items-start gap-2">
      <IconAlertTriangle size={14} className="mt-px flex-none text-warning" />
      <Typography as="muted" className="text-[11.5px] text-grey6">
        {translate(nextStep(status, autoRemindersEffective), { list })}
      </Typography>
    </div>
  )
}
